import type { ReactNode } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate, useLocation } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { selectAuthStatus } from '@/features/auth/authSlice';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Route guard.
 *
 * <strong>This is a UX control, not a security control.</strong> It decides what to render;
 * it decides nothing about what the API will return. Every endpoint enforces its own role
 * check and object-level ownership server-side (brief §9), because anything decided in the
 * browser can be bypassed by editing the bundle.
 *
 * <p>The {@code 'unknown'} status matters: on a page reload the access token is gone (it is
 * memory-only) and the startup refresh has not resolved yet. Redirecting to /login on that
 * state would bounce an authenticated user out on every reload, so it renders a spinner and
 * waits instead.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation();
  const status = useAppSelector(selectAuthStatus);

  if (status === 'unknown') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
        <CircularProgress aria-label="Restoring your session" />
      </Box>
    );
  }

  if (status !== 'authenticated') {
    // `state` carries the attempted URL so login can return the user to where they were
    // headed rather than dumping them on the dashboard.
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}
